import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth }   from '../contexts/AuthContext';
import { useLang }   from '../contexts/LangContext';
import ThemeSwitcher from '../components/ThemeSwitcher';
import LangSwitcher  from '../components/LangSwitcher';
import styles from './AuthPage.module.css';

export default function LoginPage() {
  const { login, loginWithProvider } = useAuth();
  const { t }       = useLang();
  const navigate    = useNavigate();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!email || !password) { setError(t('auth.requiredField')); return; }
    setLoading(true);
    try {
      await login(email, password);
      navigate('/app', { replace: true });
    } catch (err) {
      setError(t('auth.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  }

  async function handleSocialLogin(provider) {
    setError('');
    setLoading(true);
    try {
      await loginWithProvider(provider);
      navigate('/app', { replace: true });
    } catch (err) {
      setError(err.message || 'Social login failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.controls}>
        <LangSwitcher />
        <ThemeSwitcher />
      </div>

      {/* Left brand panel */}
      <div className={styles.brand}>
        <div className={styles.brandLogo}>
          <div className={styles.brandLogoIcon} style={{ background: 'transparent' }}>
            <img src="/logo2.png" alt="Qeduvo" style={{ width: '100%', height: '100%', borderRadius: 'inherit', objectFit: 'cover' }} />
          </div>
          <span className={styles.brandLogoText}>Qeduvo</span>
        </div>
        <h1 className={styles.brandHeading}>
          Grow faster,<br /><span>together.</span>
        </h1>
        <p className={styles.brandSub}>
          Join thousands of creators and learners in the world's best community platform.
        </p>
        <div className={styles.brandFeatures}>
          {['200+ interactive courses', 'Real-time community feed', 'Live leaderboards & XP', 'Track your daily streak'].map(f => (
            <div key={f} className={styles.brandFeature}>
              <div className={styles.brandFeatureDot} />
              {f}
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className={styles.formPanel}>
        <div className={styles.card}>
          <div className={styles.cardInner}>
            <div className={styles.logo}>
              <div className={styles.logoIcon} style={{ background: 'transparent' }}>
                <img src="/logo2.png" alt="Qeduvo" style={{ width: '100%', height: '100%', borderRadius: 'inherit', objectFit: 'cover' }} />
              </div>
              <span className={styles.logoText}>Qeduvo</span>
            </div>
            <h2 className={styles.title}>{t('auth.signin')}</h2>
            <p className={styles.sub}>Welcome back! Sign in to continue learning.</p>

            <form onSubmit={handleSubmit} className={styles.form}>
              {error && <div className={styles.error}>{error}</div>}
              <div className="form-group">
                <label className="form-label">{t('auth.email')}</label>
                <input
                  id="input-email"
                  type="email" className="form-input"
                  value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com" autoFocus
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t('auth.password')}</label>
                <input
                  id="input-password"
                  type="password" className="form-input"
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
              <button id="btn-signin" type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 6 }} disabled={loading}>
                {loading ? t('common.loading') : t('auth.signinButton')} →
              </button>
            </form>

            <div className={styles.divider}>{t('auth.or')}</div>

            <div className={styles.socialButtons}>
              <button
                type="button"
                className={`${styles.socialButton} ${styles.googleBtn}`}
                onClick={() => handleSocialLogin('google')}
                disabled={loading}
              >
                <img src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" alt="Google" className={styles.socialIcon} />
                <span>{t('auth.continueWithGoogle')}</span>
              </button>
              <button
                type="button"
                className={`${styles.socialButton} ${styles.lineBtn}`}
                onClick={() => handleSocialLogin('line')}
                disabled={loading}
              >
                <img src="https://upload.wikimedia.org/wikipedia/commons/4/41/LINE_logo.svg" alt="LINE" className={styles.socialIcon} />
                <span>{t('auth.continueWithLine')}</span>
              </button>
            </div>

            <div className={styles.footer}>
              {t('auth.noAccount')}{' '}
              <Link to="/signup" className={styles.link}>{t('auth.signup')}</Link>
            </div>

            <div className={styles.hint}>
              <span className={styles.hintLabel}>Admin:</span> admin@qeduvo.com / Admin@1234
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}